import { useState } from "react";
import type {
  CopilotAttribution,
  McpTurnSource,
  RetrievedSource,
} from "@yazamco/shared-types";
import { Icon } from "../../components/ui/Icon";
import { usedSources } from "./copilot-export";
import { normalizeCopilotAnswerUrl } from "./AnswerImage";

/** The two-letter mark the canvas sets before a source's title. */
function sourceMark(dataSource: string | undefined): string {
  if (dataSource === "oneDriveBusiness") return "OD";
  if (dataSource === "sharePoint") return "SP";
  return "M365";
}

/**
 * Where an answer came from: the links it cited, and any business system it
 * was given. Entity annotations are not listed — Copilot only mentioned those.
 *
 * Drawn as the canvas draws it: one row of pill chips under the answer, led by
 * a "Sources" label, each chip carrying its system mark and an elided title.
 * The full path is the chip's tooltip, so a long SharePoint location costs no
 * vertical space.
 */
export function SourceList({
  attributions,
  retrievedSources,
  mcpSources,
}: {
  attributions?: CopilotAttribution[];
  retrievedSources?: RetrievedSource[];
  mcpSources?: McpTurnSource[];
}): JSX.Element | null {
  const [expanded, setExpanded] = useState(false);
  const citations = usedSources(attributions);
  const retrieved = retrievedSources ?? [];
  const business = mcpSources ?? [];

  if (citations.length === 0 && business.length === 0) return null;

  /** Titles and locations for the cited links, so a filename gets a place. */
  const byUrl = new Map(
    retrieved.map((source) => [normalizeCopilotAnswerUrl(source.url), source]),
  );
  const visible = expanded ? citations : citations.slice(0, 3);

  return (
    <footer className="cp-sources">
      <span className="cp-sources-label">
        <Icon name="doc" size={14} />
        Sources
      </span>

      {visible.map((citation, index) => {
        const href = citation.seeMoreWebUrl
          ? normalizeCopilotAnswerUrl(citation.seeMoreWebUrl)
          : undefined;
        const source = href ? byUrl.get(href) : undefined;
        const title =
          source?.title || citation.providerDisplayName || `Source ${index + 1}`;
        return (
          <a
            key={href ?? `${title}-${index}`}
            className="cp-source-chip"
            href={href ?? "#"}
            target="_blank"
            rel="noopener noreferrer"
            title={source?.path ? `${title} · ${source.path}` : title}
          >
            <span className="cp-source-mark" aria-hidden>{sourceMark(source?.dataSource)}</span>
            <span>{title}</span>
          </a>
        );
      })}

      {citations.length > 3 && (
        <button
          type="button"
          className="cp-source-chip cp-source-more"
          aria-expanded={expanded}
          onClick={() => setExpanded((current) => !current)}
        >
          {expanded ? "Show fewer" : `Show all ${citations.length}`}
        </button>
      )}

      {/* Kept apart from the citations: these are records YazamcoOps fetched at
          the user's instruction, not sources Copilot said it used. */}
      {business.map((source) => (
        <span
          key={source.invocationId}
          className="cp-source-chip cp-source-business"
          title={`Business data used · ${source.serverDisplayName}`}
        >
          <Icon name="briefcase" size={12} />
          <span>{source.serverDisplayName}</span>
        </span>
      ))}
    </footer>
  );
}
